import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { useLogInteraction, useContacts, type LogInteractionInput } from '@/hooks/useClients';
import { INTERACTION_LABELS, type InteractionType } from '@/types/clients';

type Props = {
  open: boolean;
  onClose: () => void;
  clientId: string;
};

function nowLocal() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export function LogInteractionModal({ open, onClose, clientId }: Props) {
  const log = useLogInteraction();
  const { data: contacts } = useContacts(open ? clientId : undefined);

  const [form, setForm] = useState<LogInteractionInput>({
    clientId, type: 'call', subject: '', body: '',
    occurredAt: nowLocal(), contactId: undefined,
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm({
        clientId, type: 'call', subject: '', body: '',
        occurredAt: nowLocal(), contactId: undefined,
      });
      setError(null);
    }
  }, [open, clientId]);

  function update_<K extends keyof LogInteractionInput>(key: K, val: LogInteractionInput[K]) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await log.mutateAsync({
        ...form,
        occurredAt: new Date(form.occurredAt).toISOString(),
      });
      toast.success('Interaction logged');
      onClose();
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Failed to log interaction.');
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      kicker="Log Interaction"
      title="Record an interaction."
      description="Track every meaningful touchpoint — calls, emails, meetings, site visits."
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={log.isPending}>Cancel</Button>
          <Button type="submit" form="log-form" loading={log.isPending}>Log</Button>
        </>
      }
    >
      <form id="log-form" onSubmit={onSubmit} className="space-y-5 py-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Type"
            value={form.type}
            onChange={(e) => update_('type', e.target.value as InteractionType)}
            options={Object.entries(INTERACTION_LABELS).map(([v, l]) => ({ value: v, label: l }))}
          />
          <Input
            label="When"
            type="datetime-local"
            value={form.occurredAt}
            onChange={(e) => update_('occurredAt', e.target.value)}
            required
          />
        </div>

        <Input
          label="Subject"
          required
          placeholder="e.g. Discussed Walnut spec for downtown lobby"
          value={form.subject}
          onChange={(e) => update_('subject', e.target.value)}
        />

        {contacts && contacts.length > 0 && (
          <Select
            label="Contact (optional)"
            value={form.contactId ?? ''}
            onChange={(e) => update_('contactId', e.target.value || undefined)}
            options={[
              { value: '', label: '— None —' },
              ...contacts.map((c) => ({ value: c.id, label: `${c.firstName} ${c.lastName}${c.title ? ` · ${c.title}` : ''}` })),
            ]}
          />
        )}

        <Textarea
          label="Notes"
          rows={5}
          placeholder="Details, next steps, decisions, concerns…"
          value={form.body ?? ''}
          onChange={(e) => update_('body', e.target.value)}
        />

        {error && (
          <div className="text-xs text-danger border-l-2 border-danger pl-3 py-1">{error}</div>
        )}
      </form>
    </Modal>
  );
}
